import React from 'react';
import userDefinedFunctionHelper from '../../../infrastructure/helpers/functions/userDefinedFunctions/userDefinedFunctionHelper';
import referenceValidator from '../../../infrastructure/helpers/functions/validator/referenceValidator';
import reportConfigNavigation from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation';
import ReportCollections from '../../../infrastructure/types/Functions/ReportCollections';
import Assignment from '../../../types/functions/Assignment/Assignment';
import locationType from '../../../types/functions/Location/LocationType';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import ReportConfig from '../../../types/report/ReportConfig';
import UserDefinedFunction from '../../../types/report/UserDefinedFunction';
import ErrorList from '../../Common/ErrorList/ErrorList';
import ReportLogicList from '../Common/ReportLogicList/ReportLogicList';
import { getColumns } from './UserDefinedFunctionBuilder.Grid';
import UserDefinedFunctionSelector from './UserDefinedFunctionSelector/UserDefinedFunctionSelector';
import WarningModal from '../../Common/Modals/WarningModal/WarningModal';
import { reportType } from '../../../infrastructure/constants/reportType';
import ReportComponentStatus from '../Common/ReportComponentStatus/ReportComponentStatus';
import ComponentType from '../../../types/report/ComponentType';
import { onReferenceSelected, reportConfigPrefix } from '../../../infrastructure/helpers/report/reference/helper';
import copyObject from '../../../infrastructure/helpers/common/copyObject';
import reportLogicReferenceHelper from '../../../infrastructure/helpers/functions/report/reportLogicReferenceHelper';
import { buildText, onGlobalComponentLinkClick, prepareComponentType } from '../Common/ReferenceComponentHelper/ReferenceComponentHelper';
import Component from '../../../types/common/Component';
import httpClient from '../../../infrastructure/helpers/common/httpClient';
import CustomDropdownOption from '../../Common/Dropdown/CustomDropdownOption';

interface Props {
    isWaiting: boolean;
    isReadOnly: boolean;
    functionNumber: number | null;
    reportConfig: ReportConfig;
    collections: ReportCollections;
    onSelected: (number: number) => void;
    onChange: (dictionaries: UserDefinedFunction[]) => void;
    clipboard: ReportLogicLocation | null;
    onClipboardChange: (clipboard: ReportLogicLocation) => void;
}
interface State {
    showWarningModal: boolean;
}
class UserDefinedFunctionBuilder extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);

        this.state = {
            showWarningModal: false
        }

        this.onAddFunctionClick = this.onAddFunctionClick.bind(this);
        this.onRemoveFunctionClick = this.onRemoveFunctionClick.bind(this);
        this.onNameChange = this.onNameChange.bind(this);
        this.onReturnTypeChange = this.onReturnTypeChange.bind(this);
        this.onBusinessDescriptionChange = this.onBusinessDescriptionChange.bind(this);
        this.onFunctionChange = this.onFunctionChange.bind(this);
        this.getContent = this.getContent.bind(this);
        this.onRemoveFunctionApproved = this.onRemoveFunctionApproved.bind(this);
        this.onGlobalFunctionReferenceSelected = this.onGlobalFunctionReferenceSelected.bind(this);
        this.getUdfReferences = this.getUdfReferences.bind(this);
        this.onGlobalUdfModeClick = this.onGlobalUdfModeClick.bind(this);
    }

    onAddFunctionClick(): void {

        let udf = userDefinedFunctionHelper.getDefault();

        if (this.props.reportConfig.type === reportType.accuracy) {

            let now = new Date(Date.now());
            udf.lastUpdatedTime = now.toISOString().slice(0, -5); // Remove '.fffZ'
        }

        userDefinedFunctionHelper.add(this.props.reportConfig.userDefinedFunctions, udf);

        this.props.onChange(this.props.reportConfig.userDefinedFunctions);
    }

    async validateReferences(): Promise<string> {

        let error = referenceValidator.validateUserDefinedFunctionReference(this.props.functionNumber as number, this.props.reportConfig) || '';

        if (error) {
            return error;
        }

        if (this.props.reportConfig.type === reportType.globalModule) {

            let options = await this.getUdfReferences(this.props.functionNumber as number);

            let functionName = this.props.reportConfig.userDefinedFunctions.find(x => x.number === this.props.functionNumber)?.name || '';

            options.forEach((opt) => {
                error += `${functionName} is referenced by ${opt.component.name}. Remove references first.\r\n`;
            });
        }

        return error;
    }

    async onRemoveFunctionClick(): Promise<void> {

        let functionNumber = this.props.functionNumber || 0;

        let location = this.getLocation(functionNumber, []);

        let error = await this.validateReferences();

        if (error) {
            let target = userDefinedFunctionHelper.get(this.props.reportConfig.userDefinedFunctions, location);

            target.errors = [error];

            this.props.onChange(this.props.reportConfig.userDefinedFunctions);

            return;
        }

        this.setState({ showWarningModal: true });
    }

    onRemoveFunctionApproved() {

        let location = this.getLocation(this.props.functionNumber as number, []);

        let reduced = this.props.reportConfig.userDefinedFunctions.filter(x => x.number !== location.statementNumber);

        this.props.onChange(reduced);

        this.setState({ showWarningModal: false });
    }

    onNameChange(name: string, number: number): void {

        let location = this.getLocation(number, []);

        this.onFunctionChange(location, { name: name } as UserDefinedFunction);
    }

    async onReturnTypeChange(e: React.ChangeEvent<HTMLElement>, dataType: string, number: number, assignment: Assignment): Promise<void> {

        let references = await this.getUdfReferences(number);

        let location = this.getLocation(number, []);

        if (references.length > 0) {
            e.preventDefault();

            let configs = references.map(x => x.component.name).join(', ');

            let error = `The function is referenced in cofigs: ${configs}. Please remove references first.`

            this.onFunctionChange(location, { errors: [error] } as UserDefinedFunction);

            return;
        }


        this.onFunctionChange(location, { returnType: dataType } as UserDefinedFunction);
    }

    onBusinessDescriptionChange(businessDescription: string, number: number): void {

        let location = this.getLocation(number, []);

        this.onFunctionChange(location, { businessDescription: businessDescription } as UserDefinedFunction);
    }

    getLocation(number: number, argumentIndexes: number[]): ReportLogicLocation {

        let location = {} as ReportLogicLocation;

        location.statement = locationType.statement.userDefinedFunction;
        location.statementNumber = number;
        location.argumentIndexes = argumentIndexes;

        return location;
    }

    onFunctionChange(location: ReportLogicLocation, changes: UserDefinedFunction): void {

        let target = userDefinedFunctionHelper.get(this.props.reportConfig.userDefinedFunctions, location);

        Object.assign(target, changes);

        this.props.onChange(this.props.reportConfig.userDefinedFunctions);
    }

    getContent(func: UserDefinedFunction): JSX.Element {

        return (
            <>
                {
                    this.props.children
                }

                <ErrorList errors={func?.errors || []} className="mt-2" />

                <ReportComponentStatus
                    reportType={this.props.reportConfig.type}
                    lastUpdatedTime={func.lastUpdatedTime}
                    isReadOnly={this.props.isReadOnly} />
            </>
        );
    }

    onGlobalFunctionReferenceSelected(option: CustomDropdownOption) {
        if (option.asUrl && this.props.reportConfig.type === reportType.globalModule) {

            let type = prepareComponentType(option.component.componentType);

            onGlobalComponentLinkClick(option.component.componentNumber, type, option.component.reportConfigId, option.component.componentInnerType, option.component.mainComponentNumber);
        }
        else if (option?.component.name?.startsWith(ComponentType.UserDefinedFunction)) {
            this.props.onSelected(option.component.componentNumber);
        }
        else {
            onReferenceSelected(option.component.htmlId || '');
        }
    }

    onGlobalUdfModeClick(globalFunctionId: number) {

        let functionNumber = this.props.collections.globalModuleConfig.userDefinedFunctions.find(x => x.id === globalFunctionId)?.number as number;

        let type = prepareComponentType(ComponentType.UserDefinedFunction);

        onGlobalComponentLinkClick(functionNumber, type, this.props.collections.globalModuleConfig.id);
    }

    async getUdfReferences(number: number): Promise<CustomDropdownOption[]> {

        let config = copyObject(this.props.reportConfig);

        config.userDefinedFunctions = config.userDefinedFunctions.filter(x => x.number !== number);

        let collections = this.props.collections;

        let references = reportLogicReferenceHelper.getUserDefinedFunctionReferences(number, config, collections);

        let options: CustomDropdownOption[] = references.map((x) => { return { component: { htmlId: x.value, name: x.text, componentType: x.componentType, componentNumber: x.number, reportConfigId: x.configId, componentInnerType: x.innerType, mainComponentNumber: x.mainComponentNumber } as Component } as CustomDropdownOption });

        if (this.props.reportConfig.type === reportType.globalModule) {

            let udf = this.props.reportConfig.userDefinedFunctions.find(x => x.number === number);

            if (udf?.id) {

                let component = { componentInnerType: ComponentType.UserDefinedFunction.replaceAll(' ', ''), componentType: ComponentType.UserDefinedFunction.replaceAll(' ', ''), componentId: udf?.id, reportConfigId: config.id, componentNumber: udf?.number } as Component;

                let res = await httpClient.post('report/global-module/references', component);

                let refs = res.data as Component[];

                refs.forEach((ref) => {
                    ref.name = buildText(ref.reportConfigName || '', ref.name || '', ref.componentType);
                    options.push({
                        component: ref,
                        asUrl: true
                    } as CustomDropdownOption)
                });
            }
        }

        return options;
    }

    render(): JSX.Element {

        return (
            <>
                {this.props.functionNumber &&
                    <>
                        <div>
                            <UserDefinedFunctionSelector
                                isWaiting={this.props.isWaiting}
                                functionNumber={this.props.functionNumber || 0}
                                functions={this.props.reportConfig.userDefinedFunctions}
                                onFunctionSelected={this.props.onSelected} />
                        </div>
                        <div>
                            <ReportLogicList
                                isWaiting={this.props.isWaiting}
                                items={this.props.reportConfig.userDefinedFunctions.filter(x => x.number === this.props.functionNumber)}
                                columns={getColumns(this.onNameChange, this.onReturnTypeChange, this.onBusinessDescriptionChange, this.onRemoveFunctionClick,
                                    this.props.isReadOnly, this.onGlobalUdfModeClick, this.onGlobalFunctionReferenceSelected, this.getUdfReferences, this.props.isReadOnly, this.props.reportConfig.type)}
                                getContent={this.getContent}
                                getKey={a => a.number}
                                createHtmlId={v => reportConfigNavigation.buildFunctionId(v.number)}
                                children />
                        </div>
                    </>
                }
                <div className="row no-gutters mb-3">
                    <div className="mb-4">
                        <button
                            onClick={this.onAddFunctionClick}
                            className="btn btn-light">
                            + Function
                        </button>
                    </div>
                </div>
                {this.state.showWarningModal &&
                    <WarningModal
                        onOkClick={this.onRemoveFunctionApproved}
                        onCancelClick={() => this.setState({ showWarningModal: false })}
                        title='Remove item'
                        message='Are you sure you want to delete this item? This action cannot be undone.' />
                }
            </>
        );
    }
}

export default UserDefinedFunctionBuilder;
